import {useState, useEffect} from 'react'
import {useCompanyContext} from '../compay/CompanyContext'

const parseInstallment = (val) => {
  const parts = String(val || '').split('*')
  if (parts.length === 2) {
    const amt = Number(parts[0].trim())
    const months = Number(parts[1].trim())
    if (!isNaN(amt) && !isNaN(months) && amt > 0 && months > 0)
      return {amt, months, total: amt * months}
  }
  return null
}

const FixedInstallmentSuggestion = () => {
  const companyCtx = useCompanyContext()
  const status =
    companyCtx?.getFixedInstallmentModeStatus?.data?.fixedInstallmentMode?.[0]

  const enabled = status?.enabled ?? false
  const [values, setValues] = useState([])
  const [newEntry, setNewEntry] = useState('')
  const [entryError, setEntryError] = useState('')

  const [discounts, setDiscounts] = useState([])
  const [newDiscountName, setNewDiscountName] = useState('')
  const [newDiscountValue, setNewDiscountValue] = useState('')
  const [discountError, setDiscountError] = useState('')

  useEffect(() => {
    if (status?.values?.length) setValues(status.values)
    if (status?.discounts?.length) setDiscounts(status.discounts)
  }, [status?.values, status?.discounts])

  const save = (updatedValues, updatedDiscounts) => {
    companyCtx.postFixedInstallmentModeStatus.mutate({
      enabled: true,
      values: updatedValues,
      discounts: updatedDiscounts,
    })
  }

  const handleCheckboxChange = (e) => {
    companyCtx.postFixedInstallmentModeStatus.mutate({
      enabled: e.target.checked,
      values,
      discounts,
    })
  }

  // ── Installment plan handlers ──
  const handleAddPlan = () => {
    const trimmed = newEntry.trim()
    if (!trimmed) return
    if (!parseInstallment(trimmed)) {
      setEntryError('Invalid format — use amount*months (e.g. 15000*8)')
      return
    }
    if (values.includes(trimmed)) {
      setEntryError('This installment plan already exists')
      return
    }
    const updated = [...values, trimmed]
    setValues(updated)
    setNewEntry('')
    setEntryError('')
    save(updated, discounts)
  }

  const handleRemovePlan = (index) => {
    const updated = values.filter((_, i) => i !== index)
    setValues(updated)
    save(updated, discounts)
  }

  // ── Discount handlers ──
  const handleAddDiscount = () => {
    const name = newDiscountName.trim()
    const value = Number(newDiscountValue)
    if (!name) { setDiscountError('Please enter a discount name'); return }
    if (!newDiscountValue || isNaN(value) || value <= 0) {
      setDiscountError('Please enter a valid rupee amount')
      return
    }
    if (discounts.some((d) => d.name.toLowerCase() === name.toLowerCase())) {
      setDiscountError('A discount with this name already exists')
      return
    }
    const updated = [...discounts, {name, value}]
    setDiscounts(updated)
    setNewDiscountName('')
    setNewDiscountValue('')
    setDiscountError('')
    save(values, updated)
  }

  const handleRemoveDiscount = (index) => {
    const updated = discounts.filter((_, i) => i !== index)
    setDiscounts(updated)
    save(values, updated)
  }

  return (
    <div className='card p-5'>
      <h3 className='card-title'>Fixed Installment Mode</h3>
      <p>
        Enable this to use Admission Fees + Fixed Installment plans instead of the standard
        installment options. Add multiple plans (e.g. <strong>15000*8</strong>) and discounts.
      </p>

      <div className='form-check mb-4'>
        <input
          className='form-check-input'
          type='checkbox'
          id='fixedInstallmentModeCheckbox'
          onChange={handleCheckboxChange}
          checked={enabled}
        />
        <label className='form-check-label' htmlFor='fixedInstallmentModeCheckbox'>
          Yes, enable Fixed Installment Mode
        </label>
      </div>

      {enabled && (
        <>
          {/* ══════════════════ INSTALLMENT PLANS ══════════════════ */}
          <h5 className='fw-bold mb-3 mt-2'>Fixed Installment Plans</h5>

          {values.length > 0 && (
            <div className='table-responsive mb-4'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>Sr No</th>
                    <th>Plan (amount*months)</th>
                    <th>Per Month</th>
                    <th>Months</th>
                    <th>Total Amount</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {values.map((v, i) => {
                    const parsed = parseInstallment(v)
                    return (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td><span className='badge badge-light-primary fs-7'>{v}</span></td>
                        <td>₹{parsed ? parsed.amt.toLocaleString() : '-'}</td>
                        <td>{parsed ? parsed.months : '-'}</td>
                        <td>₹{parsed ? parsed.total.toLocaleString() : '-'}</td>
                        <td>
                          <button type='button' className='btn btn-icon btn-sm btn-light-danger'
                            onClick={() => handleRemovePlan(i)} title='Remove'>
                            <i className='bi bi-trash fs-6' />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className='d-flex align-items-start gap-3 flex-wrap mb-6'>
            <div>
              <label className='fw-bold col-form-label mb-1'>
                Add Fixed Installment Plan
                <small className='d-block text-muted fw-normal' style={{fontSize: 12}}>
                  Format: amount*months &nbsp;(e.g. <strong>15000*8</strong>)
                </small>
              </label>
              <div className='d-flex gap-2 align-items-center'>
                <input type='text' className={`form-control ${entryError ? 'is-invalid' : ''}`}
                  style={{maxWidth: 180}} placeholder='e.g. 15000*8'
                  value={newEntry}
                  onChange={(e) => { setNewEntry(e.target.value); setEntryError('') }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddPlan()}
                />
                <button type='button' className='btn btn-sm btn-primary' onClick={handleAddPlan}>
                  + Add
                </button>
              </div>
              {entryError && <div className='text-danger mt-1' style={{fontSize: 12}}>{entryError}</div>}
              {newEntry && parseInstallment(newEntry) && (
                <div className='text-muted mt-1' style={{fontSize: 12}}>
                  {(() => {
                    const p = parseInstallment(newEntry)
                    return `₹${p.amt.toLocaleString()} × ${p.months} months = ₹${p.total.toLocaleString()} total`
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* ══════════════════ DISCOUNTS ══════════════════ */}
          <hr className='my-2' />
          <h5 className='fw-bold mb-3 mt-4'>Discounts</h5>

          {discounts.length > 0 && (
            <div className='table-responsive mb-4'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>Sr No</th>
                    <th>Discount Name</th>
                    <th>Value (₹)</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((d, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td><span className='badge badge-light-success fs-7'>{d.name}</span></td>
                      <td>₹{Number(d.value).toLocaleString()}</td>
                      <td>
                        <button type='button' className='btn btn-icon btn-sm btn-light-danger'
                          onClick={() => handleRemoveDiscount(i)} title='Remove'>
                          <i className='bi bi-trash fs-6' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className='d-flex align-items-start gap-3 flex-wrap'>
            <div>
              <label className='fw-bold col-form-label mb-1'>Add Discount</label>
              <div className='d-flex gap-2 align-items-center flex-wrap'>
                <input type='text' className={`form-control ${discountError ? 'is-invalid' : ''}`}
                  style={{maxWidth: 200}} placeholder='Discount name'
                  value={newDiscountName}
                  onChange={(e) => { setNewDiscountName(e.target.value); setDiscountError('') }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddDiscount()}
                />
                <input type='number' min={0} className={`form-control ${discountError ? 'is-invalid' : ''}`}
                  style={{maxWidth: 150}} placeholder='Amount (₹)'
                  value={newDiscountValue}
                  onChange={(e) => { setNewDiscountValue(e.target.value); setDiscountError('') }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddDiscount()}
                />
                <button type='button' className='btn btn-sm btn-success' onClick={handleAddDiscount}>
                  + Add
                </button>
              </div>
              {discountError && <div className='text-danger mt-1' style={{fontSize: 12}}>{discountError}</div>}
              {newDiscountName && newDiscountValue && Number(newDiscountValue) > 0 && (
                <div className='text-muted mt-1' style={{fontSize: 12}}>
                  {newDiscountName}: ₹{Number(newDiscountValue).toLocaleString()} off
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default FixedInstallmentSuggestion
